import api from "./api";
import { getBackendUrl } from "../config";

// Solo simulacion local. El backend verifica JWT y acceso vigente al ticket.
const setting = name => window.ENV !== undefined
  ? window.ENV[name] || ""
  : import.meta.env[name] || "";

const origin = value => {
  try {
    if (typeof value !== "string" || !value || value.trim() !== value) return null;
    const url = new URL(value);
    const host = url.hostname.toLowerCase().replace(/\.$/, "");
    if (url.username || url.password || url.search || url.hash || url.pathname !== "/") return null;
    if (host === "api.mandaexpress.com.mx") return null;
    if (url.protocol !== "https:" && !(url.protocol === "http:" &&
      ["localhost", "127.0.0.1", "[::1]"].includes(host))) return null;
    return url.origin;
  } catch (_) { return null; }
};

const configuredOrigin = () => {
  if (setting("VITE_META_CALL_ACCEPT_MOCK_UI_ENABLED") !== "true") return null;
  const pinned = origin(setting("VITE_META_CALL_STAGING_API_ORIGIN"));
  return pinned && pinned === origin(getBackendUrl()) &&
    pinned === origin(api.defaults.baseURL) ? pinned : null;
};

export const isMetaCallStageControlConfigured = () => Boolean(configuredOrigin());

export const validStageCallId = callId =>
  typeof callId === "string" &&
  /^wacid\.browsermock\.1434418\.(?:ended[0-9]{2}|stage\.[0-9a-f]{32})$/.test(callId);

const readToken = () => {
  try {
    const token = JSON.parse(localStorage.getItem("token"));
    return typeof token === "string" && token.length > 0 && !/[\r\n]/.test(token)
      ? token : null;
  } catch (_) { return null; }
};

const request = async (callId, suffix, method) => {
  const base = configuredOrigin();
  const token = readToken();
  if (!base || !token || !validStageCallId(callId)) {
    throw new Error("CONTROL_NO_DISPONIBLE");
  }
  const response = await fetch(
    base + "/meta/calls/" + encodeURIComponent(callId) + suffix,
    {
      method,
      headers: {
        Authorization: "Bearer " + token,
        ...(method === "POST" ? { "Content-Type": "application/json" } : {})
      },
      body: method === "POST" ? "{}" : undefined,
      credentials: "include", cache: "no-store", redirect: "error"
    }
  );
  if (readToken() !== token || configuredOrigin() !== base) {
    throw new Error("SESION_CAMBIO");
  }
  if (!response.ok) throw new Error("HTTP_" + response.status);
  return response.json();
};

export const fetchMetaCallStageStatus = callId => request(callId, "/status-test", "GET");
export const hangupMetaCallStage = callId => request(callId, "/hangup-test", "POST");


// Crear exclusivamente una llamada de prueba en el backend aislado.
// La oferta se produce sin microfono y sin intercambio de candidatos ICE.
export const isMetaCallStageCreateConfigured = () => Boolean(
  configuredOrigin() && window.isSecureContext && window.RTCPeerConnection && window.fetch
);

export const createNextMetaCallStage = async () => {
  const base = configuredOrigin();
  const token = readToken();
  if (!isMetaCallStageCreateConfigured() || !base || !token) {
    throw new Error("CONTROL_NO_DISPONIBLE");
  }

  let peer;
  try {
    peer = new RTCPeerConnection({ iceServers: [], iceTransportPolicy: "relay" });
    peer.addTransceiver("audio", { direction: "sendrecv" });
    const offer = await peer.createOffer();
    if (
      !offer || typeof offer.sdp !== "string" ||
      !offer.sdp.startsWith("v=0") ||
      !/(?:^|\r?\n)m=audio /.test(offer.sdp) ||
      /(?:^|\r?\n)a=candidate:/.test(offer.sdp) ||
      new TextEncoder().encode(offer.sdp).length > 262144
    ) throw new Error("OFERTA_SINTETICA_INVALIDA");

    if (readToken() !== token || configuredOrigin() !== base) {
      throw new Error("SESION_CAMBIO");
    }

    // No hay reintentos: una respuesta perdida podria corresponder a
    // una llamada creada exitosamente en MariaDB.
    const response = await fetch(base + "/__meta_stage/browser/call-next", {
      method: "POST",
      headers: {
        Authorization: "Bearer " + token,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ session: { sdp_type: "offer", sdp: offer.sdp } }),
      credentials: "include", cache: "no-store", redirect: "error"
    });

    if (readToken() !== token || configuredOrigin() !== base) {
      throw new Error("SESION_CAMBIO");
    }

    if (response.status === 409) throw new Error("TEST_CALL_STILL_ACTIVE");
    if (!response.ok) throw new Error("HTTP_" + response.status);
    const data = await response.json();
    if (
      response.status !== 201 || data?.status !== "synthetic_created" ||
      !validStageCallId(data.callId) ||
      !Number.isSafeInteger(data.ticketId) || data.ticketId <= 0 ||
      data.provider_contacted !== false || data.audio_connected !== false
    ) throw new Error("RESULTADO_NO_CONFIRMADO");
    return data;
  } finally {
    if (peer) peer.close();
  }
};
